import type { AdminNoteType, Prisma } from '@duke-hq/svc-api-db';

import { djs, type Dayjs } from '@duke-hq/djs';

type CourseEnrolment = Prisma.StudentCourseEnrolmentGetPayload<{
  select: {
    courseEnrolmentScheduleItems: {
      select: {
        started_at: true;
        semester: true;
        item_type: true;
        scheduled_start_date: true;
        scheduled_due_date: true;
      };
    };
  };
}>;

export const WeeklyContactType = {
  OUTBOUND_CALL: 'OUTBOUND_CALL',
  OUTBOUND_EMAIL: 'OUTBOUND_EMAIL',
  SUPPORT_CHAT: 'SUPPORT_CHAT',
  SMS_MESSAGE: 'SMS_MESSAGE',
} as const;

export type StudentContactNoteType =
  | AdminNoteType
  | typeof WeeklyContactType.SUPPORT_CHAT
  | typeof WeeklyContactType.SMS_MESSAGE;

type CourseEnrolmentScheduleItems = CourseEnrolment['courseEnrolmentScheduleItems'];

export const WeeklyActivityState = {
  ACTIVITY_PERFORMED: 'ACTIVITY_PERFORMED',
  NO_ACTIVITY: 'NO_ACTIVITY',
  FUTURE_ACTIVITY: 'FUTURE_ACTIVITY',
  NO_CONTACT_DETAILS: 'NO_CONTACT_DETAILS',
  DISABLED_WEEK: 'DISABLED_WEEK',
} as const;

function getScheduleWeeks(
  courseScheduleItems: Pick<
    CourseEnrolmentScheduleItems[number],
    'scheduled_start_date' | 'scheduled_due_date'
  >[],
  createdAt: Date,
  weekGenerationEndDate: Date,
  semester?: number,
): {
  weekNumber: number;
  weekStart: Dayjs;
  weekEnd: Dayjs;
  overdue: boolean;
}[] {
  if (courseScheduleItems.length === 0) {
    return [];
  }

  type WeekRange = { weekStart: Dayjs; weekEnd: Dayjs; overdue: boolean };

  // Deduplicate and sort scheduled weeks
  const scheduledWeeks: WeekRange[] = Array.from(
    new Map(
      courseScheduleItems.map((item) => {
        const start = djs(item.scheduled_start_date).startOf('day');
        const end = djs(item.scheduled_due_date).startOf('day');
        return [
          `${start.valueOf()}-${end.valueOf()}`,
          { weekStart: start, weekEnd: end, overdue: false },
        ];
      }),
    ).values(),
  ).sort((a, b) => a.weekStart.valueOf() - b.weekStart.valueOf());

  const allWeeks: WeekRange[] = [];

  // Optional week 0 if enrolment created before first scheduled week
  // BUT NOT for semester 2 (semester 2 students start directly at week 1)
  let hasWeekZero: boolean = false;
  const firstWeek = scheduledWeeks[0];
  if (firstWeek) {
    const createdDate = djs(createdAt).startOf('day');
    const shouldCreateWeekZero = createdDate.isBefore(firstWeek.weekStart, 'day') && semester !== 2;

    if (shouldCreateWeekZero) {
      allWeeks.push({ weekStart: createdDate, weekEnd: firstWeek.weekStart, overdue: false });
      hasWeekZero = true;
    }
  }

  // Add scheduled weeks
  allWeeks.push(...scheduledWeeks);

  // Add overdue weeks if enrolment (today) has passed the last scheduled due date
  const dueDates = courseScheduleItems.map((item) => djs(item.scheduled_due_date).startOf('day'));
  const lastScheduledDueDate = djs.max(dueDates);

  if (lastScheduledDueDate && lastScheduledDueDate.isBefore(weekGenerationEndDate, 'day')) {
    let currentWeekStart = lastScheduledDueDate.add(1, 'day');
    while (currentWeekStart.isBefore(weekGenerationEndDate, 'day')) {
      allWeeks.push({
        weekStart: currentWeekStart,
        weekEnd: currentWeekStart.add(7, 'day'),
        overdue: true,
      });
      currentWeekStart = currentWeekStart.add(7, 'day');
    }
  }

  // Map week numbers
  return allWeeks.map((week, index) => ({
    weekNumber: hasWeekZero ? index : index + 1,
    weekStart: week.weekStart,
    weekEnd: week.weekEnd,
    overdue: week.overdue,
  }));
}

export function getEnrolmentWeeklyTracking(
  courseEnrolment: Prisma.StudentCourseEnrolmentGetPayload<{
    select: {
      created_at: true;
      student_enrolled_at: true;
      referral: {
        select: {
          id: true;
        };
      };
      completed_at: true;
      status: true;
      withdrawn_at: true;
      deleted_at: true;
      courseEnrolmentScheduleItems: {
        select: {
          semester: true;
          started_at: true;
          scheduled_start_date: true;
          scheduled_due_date: true;
          item_type: true;
        };
      };
    };
  }>,
  contactNotes: {
    created_at?: string | Date;
    note_type: StudentContactNoteType;
    label: string;
    text?: string | null;
  }[],
  semester?: number,
): {
  activities: Array<keyof typeof WeeklyActivityState>;
  contacts: Array<
    | {
        created_at?: string | Date;
        note_type: StudentContactNoteType;
        label: string;
        text?: string | null;
      }[]
    | null
  >;
  scheduleWeeks: {
    weekNumber: number;
    weekStart: Dayjs;
    weekEnd: Dayjs;
    overdue: boolean;
  }[];
} {
  const { created_at, student_enrolled_at, status, completed_at, withdrawn_at, deleted_at } =
    courseEnrolment;
  const currentSemesterScheduleItems = courseEnrolment.courseEnrolmentScheduleItems.filter((item) =>
    semester !== undefined
      ? item.semester === semester ||
        (semester === 0 && item.semester === 1) ||
        (semester === 1 && item.semester === 0)
      : true,
  );

  // Determine the end date based on enrolment status
  const isEnrolmentInactive =
    status === 'WITHDRAWN' ||
    status === 'DELETED' ||
    completed_at !== null ||
    withdrawn_at !== null ||
    deleted_at !== null;

  const weekGenerationEndDate = completed_at || withdrawn_at || deleted_at || djs().toDate();

  const scheduleWeeks = getScheduleWeeks(
    currentSemesterScheduleItems,
    created_at,
    weekGenerationEndDate,
    semester,
  );

  const activities: Array<keyof typeof WeeklyActivityState> = [];
  const contacts: Array<typeof contactNotes | null> = [];

  const now = djs();
  const studentContacts = [...(contactNotes ?? [])];
  const hasNewReferral = courseEnrolment.referral?.id !== undefined;
  const automatedSmsStartDate = djs('2026-03-09T23:46:00.000Z');

  for (const [i, { weekStart, weekEnd }] of scheduleWeeks.entries()) {
    const activitiesInWeek = currentSemesterScheduleItems.filter(
      (item) =>
        item.started_at !== null && djs(item.started_at).isBetween(weekStart, weekEnd, 'day', '[)'),
    );

    contacts[i] = studentContacts.filter((note) =>
      djs(note.created_at).isBetween(weekStart, weekEnd, 'day', '[)'),
    );

    const weekIsBeforeAutomatedSms = weekStart.isBefore(automatedSmsStartDate);
    if (weekIsBeforeAutomatedSms) {
      contacts[i].unshift({
        created_at:
          hasNewReferral &&
          student_enrolled_at !== null &&
          djs(student_enrolled_at).isAfter(weekStart)
            ? student_enrolled_at
            : weekStart.toDate(),
        note_type: WeeklyContactType.SMS_MESSAGE,
        label: 'SMS message',
        text: `Scheduled weekly engagement SMS - Week ${i + 1} of enrolment`,
      });
    }

    // Check if this week is after enrolment became inactive
    if (isEnrolmentInactive && weekStart.isAfter(djs(weekGenerationEndDate))) {
      activities.push(WeeklyActivityState.DISABLED_WEEK);
      contacts[i] = null;
      continue;
    }

    if (weekStart.isAfter(now)) {
      activities.push(WeeklyActivityState.FUTURE_ACTIVITY);
      contacts[i] = null;
      continue;
    }

    // If we are in current week and there are no activities performed, show future week
    if (
      now.isSameOrAfter(weekStart, 'day') &&
      now.isBefore(weekEnd) &&
      activitiesInWeek?.length === 0
    ) {
      activities.push(WeeklyActivityState.FUTURE_ACTIVITY);
      continue;
    }

    if (hasNewReferral && (student_enrolled_at === null || weekEnd.isBefore(student_enrolled_at))) {
      activities.push(WeeklyActivityState.NO_CONTACT_DETAILS);
      contacts[i] = null;
      continue;
    }

    activities.push(
      activitiesInWeek?.length
        ? WeeklyActivityState.ACTIVITY_PERFORMED
        : WeeklyActivityState.NO_ACTIVITY,
    );
  }

  const lastScheduledWeek = scheduleWeeks.at(-1);
  const contactsAfterScheduleWeeks = studentContacts.filter((contact) =>
    djs(contact.created_at).isAfter(lastScheduledWeek?.weekEnd),
  );

  contacts[contacts.length - 1]?.push(...contactsAfterScheduleWeeks);

  return {
    activities,
    contacts,
    scheduleWeeks: scheduleWeeks,
  };
}
